import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import DocumentUpload from "@/components/app/DocumentUpload";
import PreapprovalStatusPill from "@/components/app/PreapprovalStatusPill";

const APP_STATUS = {
  submitted: "Submitted", credit_pulled: "Reviewed", published: "Published",
  preapproved: "Pre-approved", matched: "Matched", originated: "Originated", withdrawn: "Withdrawn", closed: "Closed",
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [matchFor, setMatchFor] = useState(null);

  const load = () => api.get("/admin/applications").then((r) => setApps(r.data.items || []));
  useEffect(() => { load(); }, []);

  const view = async (appId) => {
    const r = await api.get(`/admin/applications/${appId}`);
    setActive(r.data);
  };

  const publish = async (appId) => {
    try { await api.post(`/admin/applications/${appId}/publish`); toast.success("Published to investors"); load(); if (active) view(appId); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmDeposit = async (paId) => {
    try { await api.post(`/admin/preapprovals/${paId}/deposit-received`); toast.success("Deposit confirmed"); if (active) view(active.application.application_id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-applications">
      <div className="border border-border overflow-x-auto" data-testid="admin-apps-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left">
            <Th>Buyer</Th><Th>State</Th><Th>Budget</Th><Th>Tier</Th><Th>Status</Th><Th>Pre-Appr.</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.application_id} className="border-t border-border" data-testid={`app-row-${a.application_id}`}>
                <Td>{a.full_name}</Td>
                <Td>{a.state}</Td>
                <Td className="font-mono-data">{fmtMoney(a.vehicle_preferences?.max_budget)}</Td>
                <Td className="font-mono-data">{a.derived_profile?.credit_tier || "—"}</Td>
                <Td><span className="font-overline">{APP_STATUS[a.status] || a.status}</span></Td>
                <Td className="font-mono-data">{a.preapproval_count}</Td>
                <Td>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                      onClick={() => view(a.application_id)} data-testid={`app-review-${a.application_id}`}>Review</Button>
                    {a.status === "submitted" && (
                      <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                        onClick={() => setCreditFor(a)} data-testid={`app-credit-${a.application_id}`}>Manual Credit Review</Button>
                    )}
                    {a.status === "credit_pulled" && (
                      <Button size="sm" className="rounded-none text-xs h-8"
                        onClick={() => publish(a.application_id)} data-testid={`app-publish-${a.application_id}`}>Publish</Button>
                    )}
                    {(a.status === "preapproved" || a.status === "published") && a.preapproval_count > 0 && (
                      <Button size="sm" className="rounded-none text-xs h-8"
                        onClick={() => setMatchFor(a)} data-testid={`app-match-${a.application_id}`}>Match Car</Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <Dialog open onOpenChange={() => setActive(null)}>
          <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Application — {active.application.full_name}</DialogTitle></DialogHeader>
            <ApplicationFull data={active} onConfirmDeposit={confirmDeposit} />
          </DialogContent>
        </Dialog>
      )}

      {creditFor && (
        <CreditDialog app={creditFor} onClose={() => setCreditFor(null)} onSaved={() => { setCreditFor(null); load(); }} />
      )}

      {matchFor && (
        <MatchDialog app={matchFor} onClose={() => setMatchFor(null)} onMatched={() => { setMatchFor(null); load(); }} />
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-overline text-muted-foreground">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function ApplicationFull({ data, onConfirmDeposit }) {
  const a = data.application;
  const cp = a.credit_pull;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <KV k="Name" v={a.full_name} />
        <KV k="DOB" v={a.dob} />
        <KV k="SSN (last 4)" v={`***-**-${a.ssn_last4}`} />
        <KV k="Phone" v={a.phone} />
        <KV k="Address" v={`${a.address}, ${a.city}, ${a.state} ${a.zip_code}`} />
        <KV k="Email" v={a.buyer_email} />
        <KV k="Employer" v={`${a.employment.employer} (${a.employment.role})`} />
        <KV k="Monthly Income" v={fmtMoney(a.employment.monthly_income)} />
        <KV k="Time on Job" v={`${a.employment.time_on_job_months} mo`} />
        <KV k="Residence" v={`${a.residence.rent_or_own} · ${fmtMoney(a.residence.monthly_payment)}/mo`} />
        <KV k="Max Budget" v={fmtMoney(a.vehicle_preferences.max_budget)} />
        <KV k="Body Type" v={a.vehicle_preferences.body_type} />
      </div>
      {cp && (
        <div className="border border-border p-4">
          <div className="font-overline mb-2">Manual Credit Review</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <KV k="Score" v={cp.credit_score} />
            <KV k="Derogs" v={cp.derogs_count} />
            <KV k="Public Records" v={cp.public_records} />
            <KV k="Open Accts" v={cp.open_accounts} />
            <KV k="Total Debt" v={fmtMoney(cp.total_debt)} />
            <KV k="Prior Repos" v={cp.prior_repos} />
          </div>
        </div>
      )}
      <div>
        <div className="font-overline mb-3">Pre-Approvals ({data.preapprovals.length})</div>
        {data.preapprovals.length === 0 ? <div className="text-sm text-muted-foreground">None yet.</div> : (
          <div className="border border-border divide-y divide-border">
            {data.preapprovals.map((p) => (
              <div key={p.preapproval_id} className="p-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-center text-sm" data-testid={`admin-pa-${p.preapproval_id}`}>
                <KV k="Investor" v={p.investor_name} />
                <KV k="Max Amt" v={fmtMoney(p.max_amount)} />
                <KV k="Max APR" v={fmtPct(p.max_apr)} />
                <KV k="Term" v={`${p.max_term} ${p.payment_frequency}`} />
                <div>
                  <div className="font-overline text-muted-foreground">Escrow Deposit</div>
                  <div className="font-mono-data">{fmtMoney(p.deposit?.required_amount)} · {p.deposit?.status}</div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <PreapprovalStatusPill status={p.status} />
                  {p.status === "awaiting_deposit" && (
                    <Button size="sm" className="rounded-none text-xs h-8"
                      onClick={() => onConfirmDeposit(p.preapproval_id)}
                      data-testid={`confirm-deposit-${p.preapproval_id}`}>Confirm Deposit</Button>
                  )}
                  {p.funding_deadline && p.status === "awaiting_funding" && (
                    <span className="text-[10px] text-muted-foreground font-mono-data">
                      ⏱ {new Date(p.funding_deadline).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-border pt-4">
        <DocumentUpload
          entityType="loan_application"
          entityId={a.application_id}
          isAdmin
          readOnly
          showAll
          title="Supporting Documents"
        />
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground">{k}</div>
      <div className="font-mono-data">{v ?? "—"}</div>
    </div>
  );
}

function CreditDialog({ app, onClose, onSaved }) {
  const [f, setF] = useState({ credit_score: 650, derogs_count: 0, public_records: 0, open_accounts: 0, total_debt: 0, prior_repos: 0, notes: "" });
  const save = async () => {
    try { await api.post(`/admin/applications/${app.application_id}/manual-credit-review`, f); toast.success("Manual credit review recorded"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-xl" data-testid="credit-pull-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Manual Credit Review · {app.full_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          FinanceLocal performs underwriting manually. Record the findings from the documents and information the applicant submitted; no automated credit-bureau pull is performed.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Credit Score" value={f.credit_score} onChange={(v) => setF({ ...f, credit_score: v })} testid="cp-score" />
          <NumField label="Derogs" value={f.derogs_count} onChange={(v) => setF({ ...f, derogs_count: v })} />
          <NumField label="Public Records" value={f.public_records} onChange={(v) => setF({ ...f, public_records: v })} />
          <NumField label="Open Accounts" value={f.open_accounts} onChange={(v) => setF({ ...f, open_accounts: v })} />
          <NumField label="Total Debt" value={f.total_debt} onChange={(v) => setF({ ...f, total_debt: v })} />
          <NumField label="Prior Repos" value={f.prior_repos} onChange={(v) => setF({ ...f, prior_repos: v })} />
        </div>
        <Label className="font-overline mt-2">Notes</Label>
        <Textarea rows={2} className="rounded-none" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} data-testid="cp-save">Save Manual Credit Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="font-overline mb-2 block">{label}</Label>
      <Input type="number" className="rounded-none font-mono-data" value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)} data-testid={testid} />
    </div>
  );
}

function MatchDialog({ app, onClose, onMatched }) {
  const [paList, setPaList] = useState([]);
  const [paId, setPaId] = useState("");
  const [veh, setVeh] = useState({ year: 2022, make: "", model: "", mileage: 30000, state: app.state || "TX",
    photo: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6", purchase_price: 15000 });

  useEffect(() => {
    api.get(`/admin/applications/${app.application_id}`)
      .then((r) => {
        const actives = (r.data.preapprovals || []).filter((p) => p.status === "active");
        setPaList(actives);
        if (actives[0]) setPaId(actives[0].preapproval_id);
      });
  }, [app]);

  const submit = async () => {
    if (!paId) { toast.error("Select an active pre-approval"); return; }
    try {
      const r = await api.post(`/admin/applications/${app.application_id}/match`, { ...veh, preapproval_id: paId });
      toast.success(`Loan originated · Note ${r.data.note_id} assigned`);
      onMatched();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="match-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Match Vehicle · {app.full_name}</DialogTitle></DialogHeader>

        <div>
          <Label className="font-overline mb-2 block">Active Pre-Approval to Claim</Label>
          {paList.length === 0 ? <div className="text-sm text-muted-foreground">No active (deposit-confirmed) pre-approvals.</div> : (
            <div className="border border-border divide-y divide-border">
              {paList.map((p) => (
                <label key={p.preapproval_id} className="flex items-center gap-3 p-3 cursor-pointer">
                  <input type="radio" name="pa" checked={paId === p.preapproval_id} onChange={() => setPaId(p.preapproval_id)} />
                  <div className="font-mono-data text-sm">{p.investor_name} · {fmtMoney(p.max_amount)} · {fmtPct(p.max_apr)} · {p.max_term} {p.payment_frequency}</div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <NumField label="Year" value={veh.year} onChange={(v) => setVeh({ ...veh, year: v })} testid="match-year" />
          <NumField label="Mileage" value={veh.mileage} onChange={(v) => setVeh({ ...veh, mileage: v })} />
          <Field label="Make"><Input className="rounded-none" value={veh.make} onChange={(e) => setVeh({ ...veh, make: e.target.value })} data-testid="match-make" /></Field>
          <Field label="Model"><Input className="rounded-none" value={veh.model} onChange={(e) => setVeh({ ...veh, model: e.target.value })} data-testid="match-model" /></Field>
          <Field label="State"><Input className="rounded-none" value={veh.state} onChange={(e) => setVeh({ ...veh, state: e.target.value })} /></Field>
          <NumField label="Purchase Price" value={veh.purchase_price} onChange={(v) => setVeh({ ...veh, purchase_price: v })} testid="match-price" />
          <div className="col-span-2"><Field label="Photo URL"><Input className="rounded-none" value={veh.photo} onChange={(e) => setVeh({ ...veh, photo: e.target.value })} /></Field></div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} data-testid="match-originate">Originate Loan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="font-overline mb-2 block">{label}</Label>{children}</div>;
}
